/**
 * What both ticket editors do with an AI prefill draft beyond showing it:
 * keep the identity of lines that already exist, and remember which draft the
 * save started from.
 *
 * ─── Line ids ────────────────────────────────────────────────────────────────
 *
 * A drafted line carries no id — the model proposes services, not rows. Both
 * editors used to send every drafted line as a NEW row, so AI Suggest on a
 * ticket that already had lines deleted every existing row and re-inserted it.
 * That is not cosmetic: `ticket_pay_assignments` FKs `ticket_line_items`, so a
 * re-inserted line orphans the pay attached to it, and the Phase 4 edit audit
 * reads a delete-and-insert as the human removing a line and adding another.
 *
 * So a drafted line takes the id of an existing line when they are the SAME
 * SERVICE — same line item type AND same description (case and spacing
 * ignored). Each existing id is claimed at most once, in order. Everything the
 * draft proposes still replaces the row's values (rate, quantity, techs); only
 * the row's identity survives.
 *
 * The match is deliberately narrow. Type alone would hand a "Casing crew" row's
 * id — and its pay — to a "Torque turn" line of the same type, which is a
 * wrong answer that looks right. A draft line with no exact match is a new row,
 * and an existing row nobody matched is left out of the payload, which the
 * row-scoped save reads as the hand removing it: the same outcome AI Suggest
 * has always had for it.
 *
 * ─── The draft id ────────────────────────────────────────────────────────────
 *
 * Prefill answers with `draftId` (API 0421). A line save may cite it and the
 * API links the save's `ticket_edited` audit row to the draft, which is how
 * Phase 4 round 2 learns what the human corrected. `null` means the user did
 * not use AI on this editing session, and then NOTHING is sent — an empty
 * string or a stale id is refused 400, and a wrong link is worse than none.
 */

interface DraftLineShape {
  lineItemType?: string | null;
  description?: string | null;
}

interface ExistingLineShape extends DraftLineShape {
  id?: string | null;
}

function serviceKey(li: DraftLineShape): string | null {
  const type = (li.lineItemType ?? '').trim().toLowerCase();
  const desc = (li.description ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  if (!type || !desc) return null;
  return `${type}\u0000${desc}`;
}

/**
 * The drafted lines, each with `id` set to the matching existing line's id or
 * `null`. Always sets the key, so a drafted line never carries a stray id from
 * the model's own output.
 */
export function carryExistingLineIds<T extends DraftLineShape>(
  drafted: readonly T[],
  existing: readonly ExistingLineShape[],
): Array<T & { id: string | null }> {
  const pool = new Map<string, string[]>();
  for (const row of existing) {
    if (!row.id) continue;
    const key = serviceKey(row);
    if (!key) continue;
    const ids = pool.get(key) ?? [];
    ids.push(String(row.id));
    pool.set(key, ids);
  }
  return drafted.map((li) => {
    const key = serviceKey(li);
    const id = key ? pool.get(key)?.shift() ?? null : null;
    return { ...li, id };
  });
}

/** The prefill's draft id, or null when the response carries none. */
export function readPrefillDraftId(draft: any): string | null {
  const raw = draft?.draftId ?? draft?.draft_id;
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

/**
 * The draft id a ticket CREATE may cite (API 0422), or undefined.
 *
 * Only when AI prefill ran in this session AND was drafted for the job the
 * ticket is being created on. The API answers 400 to a draft drafted for a
 * different job, which would refuse the whole create over a learning link.
 */
export function draftIdForCreate(
  draftId: string | null,
  draftJobId: string | null,
  ticketJobId: string | null | undefined,
): string | undefined {
  if (!draftId || !draftJobId || !ticketJobId) return undefined;
  return draftJobId === ticketJobId ? draftId : undefined;
}
